<script setup lang="ts">
import { ref, provide, computed, onMounted } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import AppShell from '@/components/layout/AppShell.vue'
import { onAuthStateChanged } from 'firebase/auth'
import { auth, isFirebaseConfigured } from '@/services/firebase'
import { syncUserFromFirestore, syncOrgMembersFromFirestore, syncOrgMembersToFirestore, syncUserOrgsDataFromFirestore, getUserOrgs } from '@/services/db'
import { useImpersonation } from '@/composables/useImpersonation'
import { devLog } from '@/utils/logger'

const route = useRoute()
const router = useRouter()
const activeOrgId = ref<string | null>(localStorage.getItem('activeOrgId'))
const { isImpersonating, impersonatedName, stopImpersonation } = useImpersonation()

const exitingImpersonation = ref(false)
async function handleExitImpersonation() {
  exitingImpersonation.value = true
  await stopImpersonation(activeOrgId)
  exitingImpersonation.value = false
  router.push('/admin')
}

const fullScreenRoutes = ['/', '/login', '/register', '/onboarding', '/e/', '/join/', '/enroll/']
const isFullScreen = computed(() =>
  fullScreenRoutes.some(r => (r === '/' ? route.path === '/' : route.path.startsWith(r))) ||
  route.meta.fullScreen === true
)

provide('activeOrgId', activeOrgId)
provide('isImpersonating', isImpersonating)

async function syncAuthUser(firebaseUser: { uid: string; email: string | null; displayName: string | null; photoURL: string | null }) {
  devLog.log('Auth: syncing user data...')
  localStorage.setItem('currentUserId', firebaseUser.uid)
  await syncUserFromFirestore(firebaseUser.uid, {
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL
  })
  await syncOrgMembersFromFirestore(firebaseUser.uid)
  await syncOrgMembersToFirestore(firebaseUser.uid)
  await syncUserOrgsDataFromFirestore(firebaseUser.uid)
  if (!activeOrgId.value) {
    const orgs = await getUserOrgs(firebaseUser.uid)
    if (orgs.length > 0 && orgs[0]) {
      activeOrgId.value = orgs[0].id
      localStorage.setItem('activeOrgId', orgs[0].id)
    }
  }
}

onMounted(async () => {
  if (!isFirebaseConfigured() || !auth) return

  onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      await syncAuthUser(firebaseUser)
    }
  })
})
</script>

<template>
  <div :class="{ impersonating: isImpersonating }">
    <div v-if="isImpersonating" class="impersonation-banner">
      <span class="impersonation-text">
        Viendo como: <strong>{{ impersonatedName }}</strong>
      </span>
      <span class="impersonation-badge">Modo lectura</span>
      <button class="btn-exit-impersonation" :disabled="exitingImpersonation" @click="handleExitImpersonation">
        {{ exitingImpersonation ? 'Saliendo...' : 'Salir' }}
      </button>
    </div>
    <AppShell v-if="!isFullScreen">
      <RouterView />
    </AppShell>
    <RouterView v-else />
  </div>
</template>

<style>
.impersonation-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 8px 16px;
  background: #fef3c7;
  border-bottom: 1px solid #fbbf24;
  font-size: 0.8125rem;
  color: #92400e;
  height: 36px;
  box-sizing: content-box;
}

/* Push fixed elements down by banner height when impersonating */
.impersonating .top-bar {
  top: 52px !important;
}
.impersonating .app-shell:not(.is-mobile) .main-content {
  padding-top: calc(60px + var(--space-6) + 52px) !important;
}
.impersonating .app-shell.is-mobile .main-content {
  padding-top: calc(var(--space-5) + 52px) !important;
}

.impersonation-text strong {
  font-weight: 700;
}

.impersonation-badge {
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  background: #fbbf24;
  color: #78350f;
}

.btn-exit-impersonation {
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #92400e;
  color: white;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.btn-exit-impersonation:hover:not(:disabled) {
  opacity: 0.85;
}

.btn-exit-impersonation:disabled {
  opacity: 0.5;
  cursor: wait;
}

/* Read-only guard: disable write-oriented elements, keep navigation clickable */
.impersonating button:not(.btn-exit-impersonation):not([class*="nav"]):not([class*="tab"]):not([class*="group"]):not([class*="pill"]),
.impersonating input:not(.impersonation-banner input):not([type="search"]):not([placeholder*="Buscar"]),
.impersonating select:not(.impersonation-banner select),
.impersonating textarea,
.impersonating [contenteditable] {
  pointer-events: none !important;
  opacity: 0.6 !important;
}

/* Keep nav links clickable for browsing */
.impersonating .top-bar-nav a,
.impersonating .top-bar-nav button,
.impersonating .bottom-nav a,
.impersonating .bottom-nav button,
.impersonating .top-nav-item,
.impersonating .top-nav-admin,
.impersonating .bottom-nav-item,
.impersonating .admin-tab {
  pointer-events: auto !important;
  opacity: 1 !important;
}
</style>
